import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Zap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useSite } from "../context/SiteContext";

const STORAGE_KEY = "kult_countdown_dismissed";

function diff(target) {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return { ms: 0, d: 0, h: 0, m: 0, s: 0 };
  const s = Math.floor(ms / 1000);
  return {
    ms,
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
}

const pad = (n) => String(n).padStart(2, "0");

export const DropCountdown = () => {
  const { lang } = useApp();
  const { s, site } = useSite();
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY) === "1"
  );

  // For non-lang fields read site[] directly; for lang fields use s()
  const enabledRaw = site.drop_countdown_enabled;
  const enabled = enabledRaw === true || String(enabledRaw).toLowerCase() === "true";
  const targetStr = site.drop_countdown_date || "";
  const label = s(`drop_countdown_label_${lang}`, s("drop_countdown_label_en", "INVICTA DROP"));
  const liveLabel = s(`drop_countdown_live_label_${lang}`, s("drop_countdown_live_label_en", "LIVE NOW"));
  const ctaLink = site.drop_countdown_link || "/shop";

  useEffect(() => {
    if (!enabled || dismissed) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled, dismissed]);

  if (!enabled || dismissed || !targetStr) return null;

  // `now` is read here purely to make the component re-render every second
  // when the timer interval bumps it.
  void now;

  const target = new Date(targetStr);
  if (isNaN(target.getTime())) return null;

  const t = diff(target);
  const isLive = t.ms <= 0;

  const dismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  const go = () => {
    if (ctaLink.startsWith("http")) {
      window.location.href = ctaLink;
    } else {
      navigate(ctaLink);
    }
  };

  return (
    <div
      onClick={go}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" ? go() : null)}
      className={`relative cursor-pointer select-none border-b border-kult-border ${
        isLive ? "bg-kult-yellow text-black" : "bg-kult-pink text-black"
      } hover:brightness-110 transition`}
      data-testid="drop-countdown"
    >
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-2 flex items-center justify-center gap-3 font-mono text-[11px] md:text-xs tracking-[0.3em]">
        <Zap size={14} className="shrink-0" />
        {isLive ? (
          <span data-testid="drop-countdown-live" className="font-display text-lg md:text-xl tracking-[0.15em]">
            {label} — {liveLabel} ⚡
          </span>
        ) : (
          <>
            <span className="hidden sm:inline">{label} IN</span>
            <span className="sm:hidden">{label}</span>
            <span className="font-display text-lg md:text-xl tracking-[0.15em] tabular-nums" data-testid="drop-countdown-timer">
              {t.d > 0 && <span>{pad(t.d)}<span className="text-[10px] mx-0.5 opacity-70">D</span></span>}
              <span>{pad(t.h)}<span className="text-[10px] mx-0.5 opacity-70">H</span></span>
              <span>{pad(t.m)}<span className="text-[10px] mx-0.5 opacity-70">M</span></span>
              <span>{pad(t.s)}<span className="text-[10px] mx-0.5 opacity-70">S</span></span>
            </span>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        className="absolute top-1/2 -translate-y-1/2 right-3 md:right-6 p-1 hover:bg-black/10 rounded"
        aria-label="Dismiss"
        data-testid="drop-countdown-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};
